import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { createBackgroundJob } from "@/api/background-job/createBackgroundJob";
import { getBackgroundJobKindList } from "@/api/background-job/getBackgroundJobKindList";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { ConfirmDialogQuestion } from "@/components/ConfirmDialog";
import { DetailDrawer, useDetailStackDrawer } from "@/components/detail-stack";
import { NoteAlert } from "@/components/NoteAlert";
import { toErrorText } from "@/components/snapshot/snapshot.utils";
import { DETAIL_KIND } from "@/features/detail/detailKind";
import type { DetailKind } from "@/features/detail/detailKind";
import { JOB_RUN_KINDS } from "./jobRun.kinds";
import type { JobRunInput, JobRunPrefill } from "./jobRun.types";
import { JobRunLastRun } from "./JobRunLastRun";
import { JobRunPicker } from "./JobRunPicker";

const DISCARD_QUESTION: ConfirmDialogQuestion = {
  title: "Discard this job setup?",
  body: "The job hasn't been run. Your choices will be lost.",
  confirmLabel: "Discard",
  cancelLabel: "Keep editing",
  isDanger: true,
};

/**
 * "Run a job" in a compact detail-stack drawer (`run`): pick a job kind (JOB_RUN_KINDS), set it
 * up with that kind's own settings and run it — the drawer then becomes that job's run. A kind's
 * settings can ask a question first (e.g. replacing a snapshot). Opened with a kind and input as
 * navigation state (JobRunPrefill) by "Run again".
 */
export const JobRun = () => {
  const level = useDetailStackDrawer<DetailKind>();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { jobRun?: JobRunPrefill } | null)?.jobRun ?? null;
  const kindsQuery = getBackgroundJobKindList.useQuery();
  const kinds = (kindsQuery.data ?? []).filter((entry) => entry.kind in JOB_RUN_KINDS);
  const [kindName, setKindName] = useState<string | null>(prefill?.kind ?? null);
  const [editedInput, setEditedInput] = useState<JobRunInput | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogQuestion | null>(null);
  const [question, setQuestion] = useState<ConfirmDialogQuestion | null>(null);
  const [startJob, startState] = createBackgroundJob.useMutation();
  const kind = kinds.find((entry) => entry.kind === kindName);
  let runKind = undefined;
  if (kindName) runKind = JOB_RUN_KINDS[kindName];
  let input: JobRunInput = editedInput ?? {};
  if (editedInput === null && runKind && prefill?.kind === kindName) input = runKind.toInitialInput(prefill.input);
  const canRun = kind !== undefined && runKind !== undefined && runKind.isComplete(input) && !startState.isLoading;

  const handleSelect = (nextKind: string) => {
    if (nextKind === kindName) return;
    setKindName(nextKind);
    setEditedInput(JOB_RUN_KINDS[nextKind].toInitialInput(null));
    setConfirm(null);
    setIsDirty(true);
  };

  const handleInputChange = (next: JobRunInput) => {
    setEditedInput(next);
    setIsDirty(true);
  };

  const run = async () => {
    if (!kindName) return;
    const started = await startJob({ kind: kindName, input });
    if (started.data) navigate(level.toSwap(DETAIL_KIND.jobs, started.data.id), { replace: true });
  };

  const handleRun = () => {
    if (confirm) {
      setQuestion(confirm);
      return;
    }
    void run();
  };

  const handleConfirm = () => {
    setQuestion(null);
    void run();
  };

  const handleCancel = () => {
    setQuestion(null);
  };

  const title = (
    <Typography
      sx={{ fontWeight: 700 }}
      variant="h2"
    >
      Run a job
    </Typography>
  );

  const footer = (
    <>
      <Button onClick={level.close}>Cancel</Button>
      <Button
        disableElevation
        disabled={!canRun}
        onClick={handleRun}
        variant="contained"
      >
        {startState.isLoading ? "Starting…" : "Run now"}
      </Button>
    </>
  );

  return (
    <DetailDrawer
      confirmClose={isDirty ? DISCARD_QUESTION : null}
      footer={footer}
      title={title}
    >
      <Stack spacing={3}>
        {kindsQuery.error && (
          <NoteAlert severity="error">Couldn't load the jobs: {toErrorText(kindsQuery.error)}</NoteAlert>
        )}
        <JobRunPicker
          kinds={kinds}
          onSelect={handleSelect}
          selected={kindName}
        />
        {!kind && <NoteAlert severity="info">Choose a job to see its settings.</NoteAlert>}
        {kind && runKind && (
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{kind.label}</Typography>
              <JobRunLastRun kind={kind.kind} />
            </Box>
            <runKind.Settings
              input={input}
              key={kind.kind}
              onChange={handleInputChange}
              onConfirmChange={setConfirm}
            />
            {startState.error && <NoteAlert severity="error">{toErrorText(startState.error)}</NoteAlert>}
          </Stack>
        )}
      </Stack>
      <ConfirmDialog
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        question={question}
      />
    </DetailDrawer>
  );
};
